import { Link } from 'react-router-dom';
import { Cake, CheckCircle2, ChevronRight, User } from 'lucide-react';
import { SafeImage } from '@/shared/ui';
import { AcaoTelaMia } from './AcaoTelaMia';
import type { AcaoMia, BlocoMia } from './assistenteService';
import styles from './BlocosMia.module.css';

type Props = {
  blocos: BlocoMia[];
  /** Botao "perguntar" e chip de sugestao mandam o texto como fala da pessoa. */
  onPerguntar: (texto: string) => void;
  desabilitado?: boolean;
};

function BotaoAcao({ acao, onPerguntar, desabilitado, discreto }: {
  acao: AcaoMia;
  onPerguntar: (texto: string) => void;
  desabilitado?: boolean;
  discreto?: boolean;
}) {
  const classe = discreto ? `${styles.acao} ${styles.acaoDiscreta}` : styles.acao;
  if (acao.tipo === 'navegar' && acao.path) {
    return (
      <Link className={classe} to={acao.path}>
        {acao.rotulo}
      </Link>
    );
  }
  return (
    <button
      type="button"
      className={classe}
      disabled={desabilitado}
      onClick={() => acao.texto && onPerguntar(acao.texto)}
    >
      {acao.rotulo}
    </button>
  );
}

function Foto({ url, nome }: { url?: string; nome: string }) {
  return (
    <span className={styles.foto} aria-hidden>
      {url ? <SafeImage src={url} alt={nome} variant="avatar" /> : <User size={18} />}
    </span>
  );
}

export function BlocosMia({ blocos, onPerguntar, desabilitado }: Props) {
  if (blocos.length === 0) return null;
  return (
    <div className={styles.blocos}>
      {blocos.map((bloco, indice) => {
        const chave = `${bloco.tipo}-${indice}`;
        switch (bloco.tipo) {
          case 'cartao_pessoa':
            return (
              <section key={chave} className={styles.cartao} aria-label={bloco.nome}>
                <div className={styles.cartaoTopo}>
                  <Foto url={bloco.foto} nome={bloco.nome} />
                  <div className={styles.cartaoNome}>
                    <strong>{bloco.nome}</strong>
                    {bloco.selo === 'ANIVERSARIO' ? (
                      <span className={styles.selo}>
                        <Cake size={12} aria-hidden /> Aniversário hoje
                      </span>
                    ) : null}
                  </div>
                </div>
                {bloco.linhas?.length ? (
                  <dl className={styles.linhas}>
                    {bloco.linhas.map((linha) => (
                      <div key={linha.rotulo} className={styles.linhaPar}>
                        <dt>{linha.rotulo}</dt>
                        <dd>{linha.valor}</dd>
                      </div>
                    ))}
                  </dl>
                ) : null}
                {bloco.acoes?.length ? (
                  <div className={styles.acoes}>
                    {bloco.acoes.map((acao) => (
                      <BotaoAcao key={acao.rotulo} acao={acao} onPerguntar={onPerguntar} desabilitado={desabilitado} />
                    ))}
                  </div>
                ) : null}
              </section>
            );
          case 'lista':
            return (
              <section key={chave} className={styles.lista} aria-label={bloco.titulo ?? 'Lista'}>
                {bloco.titulo ? (
                  <p className={styles.tituloBloco}>
                    {bloco.titulo}
                    {bloco.total && bloco.total > bloco.itens.length ? (
                      <span className={styles.total}> · {bloco.itens.length} de {bloco.total}</span>
                    ) : null}
                  </p>
                ) : null}
                <ul>
                  {bloco.itens.map((item, i) => {
                    const corpo = (
                      <>
                        <Foto url={item.foto} nome={item.titulo} />
                        <span className={styles.itemTexto}>
                          <span className={styles.itemTitulo}>{item.titulo}</span>
                          {item.subtitulo ? <span className={styles.itemSub}>{item.subtitulo}</span> : null}
                        </span>
                        {item.acao ? <ChevronRight size={16} className={styles.seta} aria-hidden /> : null}
                      </>
                    );
                    const acao = item.acao;
                    return (
                      <li key={`${item.titulo}-${i}`}>
                        {acao?.tipo === 'navegar' && acao.path ? (
                          <Link className={styles.item} to={acao.path}>{corpo}</Link>
                        ) : acao?.tipo === 'perguntar' && acao.texto ? (
                          <button
                            type="button"
                            className={styles.item}
                            disabled={desabilitado}
                            onClick={() => onPerguntar(acao.texto!)}
                          >
                            {corpo}
                          </button>
                        ) : (
                          <div className={styles.item}>{corpo}</div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          case 'resumo':
            return (
              <section key={chave} className={styles.resumo} aria-label={bloco.titulo ?? 'Resumo'}>
                {bloco.titulo ? <p className={styles.tituloBloco}>{bloco.titulo}</p> : null}
                <dl className={styles.linhas}>
                  {bloco.itens.map((linha) => (
                    <div key={linha.rotulo} className={styles.linhaPar}>
                      <dt>{linha.rotulo}</dt>
                      <dd className={linha.destaque ? styles.destaque : undefined}>{linha.valor}</dd>
                    </div>
                  ))}
                </dl>
                {bloco.rodape ? <p className={styles.rodape}>{bloco.rodape}</p> : null}
              </section>
            );
          case 'tabela':
            return (
              <section key={chave} className={styles.tabelaWrap} aria-label={bloco.titulo ?? 'Tabela'}>
                {bloco.titulo ? <p className={styles.tituloBloco}>{bloco.titulo}</p> : null}
                <div className={styles.tabelaRolagem}>
                  <table className={styles.tabela}>
                    <thead>
                      <tr>{bloco.colunas.map((c, i) => <th key={i}>{c}</th>)}</tr>
                    </thead>
                    <tbody>
                      {bloco.linhas.map((linha, i) => (
                        <tr key={i}>{linha.map((c, j) => <td key={j}>{c}</td>)}</tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            );
          case 'botoes':
            return (
              <div key={chave} className={styles.acoes}>
                {bloco.acoes.map((acao) => (
                  <BotaoAcao key={acao.rotulo} acao={acao} onPerguntar={onPerguntar} desabilitado={desabilitado} />
                ))}
              </div>
            );
          case 'sugestoes':
            return (
              <div key={chave} className={styles.sugestoes} aria-label="Sugestões">
                {bloco.itens.map((item) => (
                  <button
                    key={item}
                    type="button"
                    className={styles.chip}
                    disabled={desabilitado}
                    onClick={() => onPerguntar(item)}
                  >
                    {item}
                  </button>
                ))}
              </div>
            );
          case 'resultado':
            return (
              <section key={chave} className={styles.resultado} aria-label={bloco.titulo}>
                <CheckCircle2 size={18} className={styles.resultadoIcone} aria-hidden />
                <div>
                  <strong>{bloco.titulo}</strong>
                  {bloco.texto ? <p>{bloco.texto}</p> : null}
                  {bloco.acao ? (
                    <BotaoAcao acao={bloco.acao} onPerguntar={onPerguntar} desabilitado={desabilitado} discreto />
                  ) : null}
                </div>
              </section>
            );
          case 'imagens':
            return (
              <div key={chave} className={styles.imagens}>
                {bloco.itens.map((imagem) => (
                  <figure key={imagem.url} className={styles.figura}>
                    <a href={imagem.url} target="_blank" rel="noreferrer noopener">
                      <SafeImage src={imagem.url} alt={imagem.legenda ?? ''} />
                    </a>
                    {imagem.legenda ? <figcaption>{imagem.legenda}</figcaption> : null}
                  </figure>
                ))}
              </div>
            );
          case 'acao_interface':
            return <AcaoTelaMia key={chave} acao={bloco.acao} />;
          default:
            return null;
        }
      })}
    </div>
  );
}
